import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { useLockBodyScroll } from 'react-use'
import { Modal, ModalProps } from '../components/shared/Modal'

export const useModal = ({
  onCloseCallback,
  isOpen: initialIsOpen = false,
  ...componentProps
}: UseModalOptions): UseModalResult => {
  const [isOpen, setIsOpen] = React.useState(initialIsOpen)

  useLockBodyScroll(isOpen)

  const toggleModal = React.useCallback(() => {
    setIsOpen(() => {
      if (onCloseCallback) onCloseCallback()

      return !isOpen
    })
  }, [isOpen, onCloseCallback])

  const ModalComponent: React.FC<React.PropsWithChildren<ModalProps>> =
    React.useMemo(
      () =>
        ({ children, ...rest }) => {
          const modalRoot = document.getElementById('modal-root')

          if (!modalRoot || !isOpen) return null

          const portalContent = <Modal {...rest}>{children}</Modal>

          return ReactDOM.createPortal(
            portalContent,
            modalRoot
          ) as React.ReactPortal
        },
      [isOpen]
    )

  return {
    isOpen,
    toggleModal,
    Modal: ModalComponent,
    modalProps: {
      toggleModal,
      ...componentProps
    }
  }
}

interface UseModalOptions extends Omit<ModalProps, 'toggleModal'> {
  isOpen?: boolean
  onCloseCallback?: () => void
}

export interface UseModalResult {
  isOpen?: boolean
  toggleModal: () => void
  Modal: React.FC<React.PropsWithChildren<ModalProps>>
  modalProps: ModalProps
}
